import { t } from '@/i18n/i18n'
import { ErrorService } from '@/shared/service/errorService'
import { FilesService } from '@/shared/service/files.service'
import { SnackbarService } from '@/shared/snackbar/snackbar.service'
import { create } from '@/shared/utils/zustandVue'
import { cloneDeep } from '@/utils'

import {
  makeMenuElement,
  type MenuElement,
} from '../menuEntries/MenuEntry.type'
import { GalleryService } from './gallery.service'
import { type GalleryItem } from './gallery.type'

class GalleryState {
  gallery: Record<string, GalleryItem> = {}
  loading = false
  editingGalleryItemId = ''
  deletingGalleryItemId = ''
  showAddingModal = false
  thumbnailImage = ''
  thumbnailImageCrop = ''
  image = ''
  galleryEntry: MenuElement = makeMenuElement()
  pdfFilename = ''
}

type GalleryStore = GalleryState & {
  load: () => void
  unload: () => void
  setAddingItem: () => void
  setThumbnailCrop: (crop: string) => void
  setThumbnail: (img: string) => void
  setDeleting: (id: string) => void
  setNotDeleting: () => void
  resetAdding: () => void
  getGalleryItems: () => Promise<void>
  uploadFile: (file: File) => Promise<void>
  uploadThumbnailImage: () => Promise<void>
  changeVisibility: (id: string) => Promise<void>
  delete: () => Promise<void>
  addGalleryItem: (gallery: GalleryItem) => Promise<void>
  reorder: (newOrder: GalleryItem[]) => Promise<void>
  getFileInfo: () => Promise<void>
  setEditing: (id: string) => Promise<void>
}

export const useGalleryStore = create<GalleryStore>((set, get) => ({
  ...new GalleryState(),
  load: () => {
    set({ loading: true })
  },
  unload: () => {
    set({ loading: false })
  },
  setAddingItem: () => {
    set({ editingGalleryItemId: '', pdfFilename: '', showAddingModal: true })
  },
  setThumbnailCrop: (crop) => {
    set({ thumbnailImageCrop: crop })
  },
  setThumbnail: (img) => {
    set({ thumbnailImage: img })
  },
  setDeleting: (id) => {
    set({ deletingGalleryItemId: id })
  },
  setNotDeleting: () => {
    set({ deletingGalleryItemId: '' })
  },
  resetAdding: () => {
    set({
      image: '',
      thumbnailImage: '',
      thumbnailImageCrop: '',
      editingGalleryItemId: '',
      showAddingModal: false,
      pdfFilename: '',
    })
  },
  async getGalleryItems() {
    try {
      get().load()
      const galleryItems: GalleryItem[] = await GalleryService.getAll()
      set({
        gallery: galleryItems.reduce<Record<string, GalleryItem>>(
          (acc, item) => {
            acc[item._id] = item
            return acc
          },
          {},
        ),
      })
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().unload()
    }
  },
  async uploadFile(file) {
    try {
      get().load()
      const { filename } = await FilesService.uploadFile(file)
      set({ image: filename })
      await get().getFileInfo()
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().unload()
    }
  },
  async uploadThumbnailImage() {
    try {
      get().load()
      const image = FilesService.getImgFromCrop(get().thumbnailImageCrop)
      const { filename } = await FilesService.uploadImage(image)
      get().setThumbnail(filename)
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().unload()
    }
  },
  async changeVisibility(id) {
    try {
      get().load()
      const gallery: GalleryItem = cloneDeep(get().gallery[id])
      gallery.visible = !gallery.visible
      await GalleryService.updateGalleryItem(gallery)
      if (gallery.visible) {
        SnackbarService.info(
          t('GALLERY.SNACKBAR.HIDE_OFF', {
            string: gallery.titles.fr,
          }),
        )
      } else {
        SnackbarService.info(
          t('GALLERY.SNACKBAR.HIDE', {
            string: gallery.titles.fr,
          }),
        )
      }
      await get().getGalleryItems()
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().unload()
    }
  },
  async delete() {
    try {
      get().load()
      await GalleryService.delete(get().deletingGalleryItemId)
      get().setNotDeleting()
      SnackbarService.info(t('GALLERY.SNACKBAR.DELETE'))
      await get().getGalleryItems()
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().unload()
    }
  },
  async addGalleryItem(gallery) {
    try {
      get().load()
      if (get().thumbnailImageCrop) {
        await get().uploadThumbnailImage()
      }
      gallery.thumbnailImage = get().thumbnailImage
      gallery.image = get().image
      let message = ''
      if (get().editingGalleryItemId) {
        await GalleryService.updateGalleryItem(gallery)
        message = t('GALLERY.SNACKBAR.EDIT', {
          string: gallery.titles.fr,
        })
      } else {
        await GalleryService.postGallery(gallery)
        message = t('GALLERY.SNACKBAR.ADD', {
          string: gallery.titles.fr,
        })
      }
      SnackbarService.info(message)
      get().resetAdding()
      await get().getGalleryItems()
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().unload()
    }
  },
  async reorder(newOrder) {
    try {
      get().load()
      let weightCounter = 0
      const gallery = { ...get().gallery }
      newOrder.forEach((item) => {
        gallery[item._id] = { ...gallery[item._id], weight: weightCounter }
        weightCounter++
      })
      set({ gallery })
      await GalleryService.updateGalleryOrder(newOrder.map((i) => i._id))
      await get().getGalleryItems()
      SnackbarService.info(t('GALLERY.SNACKBAR.REORDER'))
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().unload()
    }
  },
  async getFileInfo() {
    try {
      get().load()
      const fileInfo = await FilesService.getFileInfo(get().image)
      set({ pdfFilename: fileInfo.fileName })
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().unload()
    }
  },
  async setEditing(id) {
    try {
      get().load()
      const fileInfo = await FilesService.getFileInfo(get().gallery[id].image)
      set({
        image: get().gallery[id].image,
        thumbnailImage: get().gallery[id].thumbnailImage,
        editingGalleryItemId: id,
        pdfFilename: fileInfo.fileName,
        showAddingModal: true,
      })
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().unload()
    }
  },
}))

// --- Thin wrappers preserving the createStore facade API ---
